/**
 * Microsoft Outlook Calendar Sync Service
 * 
 * Handles pushing AX1 calendar events to Microsoft Outlook Calendar
 * with automatic Teams meeting link attachment.
 * 
 * Sync Principles:
 * - AX1 is the single source of truth
 * - Create or update events only (never delete automatically)
 * - Syncs meetings only (not milestones/tasks/projects)
 * - Automatically attaches Teams meeting links
 * - Outlook edits are read-only in AX1
 * - All operations are logged for audit
 * 
 * @module OutlookCalendarService
 */

import type { CalendarEvent, ExternalSyncInfo } from '../types';
import type { AsyncResult, CalendarConnectionError, CalendarErrorCode } from './types';
import { MicrosoftCalendarAuthService } from './MicrosoftCalendarAuthService';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Microsoft Graph API base URL.
 */
const GRAPH_API = 'https://graph.microsoft.com/v1.0';

/**
 * Event types eligible for sync to Outlook (meetings only).
 */
const SYNCABLE_EVENT_TYPES: CalendarEvent['type'][] = ['meeting'];

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of syncing a single event to Outlook.
 */
export interface OutlookSyncResult {
  /** AX1 event ID */
  ax1EventId: string;
  
  /** Outlook event ID (if created/updated) */
  outlookEventId?: string;
  
  /** Teams meeting join URL (if attached) */
  teamsMeetingUrl?: string;
  
  /** Operation performed */
  operation: 'created' | 'updated' | 'skipped' | 'error';
  
  /** Reason for skip or error details */
  message?: string;
  
  /** Updated sync info to persist on the AX1 event */
  syncInfo?: ExternalSyncInfo;
  
  /** Timestamp of sync attempt */
  timestamp: string;
}

/**
 * Result of a batch sync operation to Outlook.
 */
export interface OutlookBatchSyncResult {
  /** Total events processed */
  total: number;
  
  /** Successfully created events */
  created: number;
  
  /** Successfully updated events */
  updated: number;
  
  /** Skipped events (not eligible) */
  skipped: number;
  
  /** Failed events */
  errors: number;
  
  /** Individual results */
  results: OutlookSyncResult[];
  
  /** Sync timestamps */
  startedAt: string;
  completedAt: string;
}

/**
 * Microsoft Graph event resource (subset of fields we use).
 */
interface OutlookCalendarEvent {
  id?: string;
  subject: string;
  body?: {
    contentType: 'text' | 'html';
    content: string;
  };
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location?: {
    displayName: string;
  };
  attendees?: Array<{
    emailAddress: {
      address: string;
      name?: string;
    };
    type: 'required' | 'optional';
  }>;
  isOnlineMeeting?: boolean;
  onlineMeetingProvider?: 'teamsForBusiness' | 'skypeForBusiness' | 'skypeForConsumer';
  onlineMeeting?: {
    joinUrl: string;
  };
  lastModifiedDateTime?: string;
  isCancelled?: boolean;
  // Extended properties for AX1 metadata
  singleValueExtendedProperties?: Array<{
    id: string;
    value: string;
  }>;
}

/**
 * Teams online meeting info from Graph API.
 * Reserved for future use when expanding Teams integration.
 */
interface _TeamsMeetingInfo {
  joinUrl: string;
  conferenceId?: string;
  tollNumber?: string;
}

/**
 * Outlook sync log entry.
 */
export interface OutlookSyncLogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  operation: string;
  eventId?: string;
  outlookEventId?: string;
  message: string;
  details?: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a structured error object.
 */
function createError(
  code: CalendarErrorCode,
  message: string,
  details?: string,
  recoverable = true,
  suggestedAction?: string
): CalendarConnectionError {
  return {
    code,
    message,
    details,
    recoverable,
    suggestedAction,
    occurredAt: new Date().toISOString(),
  };
}

/**
 * Checks if an event type is eligible for Outlook sync.
 * Only meetings are synced to Outlook.
 */
function isEventSyncableToOutlook(event: CalendarEvent): boolean {
  return SYNCABLE_EVENT_TYPES.includes(event.type);
}

/**
 * Converts AX1 event to Outlook Calendar event format.
 */
function convertToOutlookEvent(
  event: CalendarEvent,
  includeTeamsMeeting: boolean
): Omit<OutlookCalendarEvent, 'id'> {
  const eventDate = new Date(event.date);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Build start and end times
  let startDateTime: string;
  let endDateTime: string;
  
  if (event.time) {
    const [hours, minutes] = event.time.split(':').map(Number);
    const startDate = new Date(eventDate);
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate);
    const duration = event.duration || 1;
    endDate.setHours(endDate.getHours() + duration);
    
    // Format as local ISO string without timezone offset
    startDateTime = formatLocalDateTime(startDate);
    endDateTime = formatLocalDateTime(endDate);
  } else {
    // Default to 9 AM - 10 AM for events without time
    const startDate = new Date(eventDate);
    startDate.setHours(9, 0, 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setHours(10, 0, 0, 0);
    
    startDateTime = formatLocalDateTime(startDate);
    endDateTime = formatLocalDateTime(endDate);
  }
  
  // Build HTML body with AX1 metadata
  const bodyContent = buildEventBody(event);
  
  // Build attendees list (if we have email addresses)
  const attendees = event.attendees?.map(name => ({
    emailAddress: {
      name,
      address: '', // We don't have emails, Graph API will skip invalid ones
    },
    type: 'required' as const,
  })).filter(a => a.emailAddress.name); // Only include if we have a name
  
  const outlookEvent: Omit<OutlookCalendarEvent, 'id'> = {
    subject: `📅 ${event.title}`,
    body: {
      contentType: 'html',
      content: bodyContent,
    },
    start: {
      dateTime: startDateTime,
      timeZone,
    },
    end: {
      dateTime: endDateTime,
      timeZone,
    },
    location: event.location ? { displayName: event.location } : undefined,
    attendees: attendees?.length ? attendees : undefined,
  };
  
  // Add Teams meeting if requested
  if (includeTeamsMeeting) {
    outlookEvent.isOnlineMeeting = true;
    outlookEvent.onlineMeetingProvider = 'teamsForBusiness';
  }
  
  return outlookEvent;
}

/**
 * Formats a date to local ISO string format (without Z suffix).
 */
function formatLocalDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/**
 * Builds HTML body content for Outlook event.
 */
function buildEventBody(event: CalendarEvent): string {
  const parts: string[] = [];
  
  if (event.description) {
    parts.push(`<p>${escapeHtml(event.description)}</p>`);
  }
  
  parts.push('<hr/>');
  parts.push('<p><strong>📌 Synced from AX1 | Capital</strong></p>');
  parts.push('<ul>');
  parts.push(`<li>Type: ${event.type.charAt(0).toUpperCase() + event.type.slice(1)}</li>`);
  
  if (event.project) {
    parts.push(`<li>Project: ${escapeHtml(event.project)}</li>`);
  }
  if (event.priority) {
    parts.push(`<li>Priority: ${event.priority}</li>`);
  }
  if (event.status) {
    parts.push(`<li>Status: ${event.status}</li>`);
  }
  if (event.assignee) {
    parts.push(`<li>Assignee: ${escapeHtml(event.assignee)}</li>`);
  }
  
  parts.push('</ul>');
  parts.push('<p><em>⚠️ This meeting is managed by AX1. Edits here will not sync back.</em></p>');
  parts.push(`<p style="color:#888;font-size:11px;">AX1 Event ID: ${event.id}</p>`);
  
  return parts.join('\n');
}

/**
 * Escapes HTML special characters.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// ─────────────────────────────────────────────────────────────────────────────
// Outlook Calendar Service Class
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Service for syncing AX1 events to Microsoft Outlook Calendar.
 * 
 * Usage:
 * ```typescript
 * const service = OutlookCalendarService.getInstance();
 * const result = await service.createOutlookMeeting(ax1Event, true);
 * if (result.success) {
 *   console.log('Teams URL:', result.data.teamsMeetingUrl);
 * }
 * ```
 */
export class OutlookCalendarService {
  private static instance: OutlookCalendarService | null = null;
  
  // Auth service for token management
  private authService: MicrosoftCalendarAuthService;
  
  // Sync log (in-memory)
  private syncLog: OutlookSyncLogEntry[] = [];
  
  // Maximum log entries
  private readonly MAX_LOG_ENTRIES = 1000;

  // ─────────────────────────────────────────────────────────────────────────
  // Constructor & Singleton
  // ─────────────────────────────────────────────────────────────────────────

  private constructor() {
    this.authService = MicrosoftCalendarAuthService.getInstance();
  }

  /**
   * Get singleton instance of the service.
   */
  public static getInstance(): OutlookCalendarService {
    if (!OutlookCalendarService.instance) {
      OutlookCalendarService.instance = new OutlookCalendarService();
    }
    return OutlookCalendarService.instance;
  }

  /**
   * Reset singleton instance (for testing purposes).
   */
  public static resetInstance(): void {
    OutlookCalendarService.instance = null;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Create Meeting
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Creates a meeting in Outlook Calendar with optional Teams meeting link.
   * 
   * @param event - AX1 calendar event (must be type 'meeting')
   * @param attachTeamsMeeting - Whether to automatically attach a Teams meeting link
   * @returns Sync result with Outlook event ID and Teams URL
   */
  public async createOutlookMeeting(
    event: CalendarEvent,
    attachTeamsMeeting = true
  ): Promise<AsyncResult<OutlookSyncResult>> {
    const timestamp = new Date().toISOString();
    
    try {
      // Check if event is eligible (meetings only)
      if (!isEventSyncableToOutlook(event)) {
        const result: OutlookSyncResult = {
          ax1EventId: event.id,
          operation: 'skipped',
          message: `Only meetings can be synced to Outlook. Event type '${event.type}' is not supported.`,
          timestamp,
        };
        
        this.log('info', 'createOutlookMeeting', event.id, undefined, result.message);
        
        return { success: true, data: result };
      }
      
      // Ensure we're connected and have a valid token
      const tokenResult = await this.authService.getValidAccessToken();
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }
      
      // Check if event already has an Outlook event ID (update vs create)
      const existingOutlookEventId = event.externalSync?.provider === 'microsoft'
        ? event.externalSync.externalEventId
        : undefined;
      
      if (existingOutlookEventId) {
        // Update existing event
        return this.updateOutlookMeetingInternal(
          event,
          existingOutlookEventId,
          attachTeamsMeeting,
          tokenResult.data
        );
      }
      
      // Create new event
      return this.createOutlookMeetingInternal(
        event,
        attachTeamsMeeting,
        tokenResult.data
      );
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'createOutlookMeeting', event.id, undefined, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to create Outlook meeting',
          errorMessage,
          true,
          'Please try again'
        ),
      };
    }
  }

  /**
   * Attaches a Teams meeting link to an existing Outlook calendar event.
   * 
   * @param event - AX1 event with existing Outlook sync info
   * @returns Updated sync result with Teams URL
   */
  public async attachTeamsMeeting(
    event: CalendarEvent
  ): Promise<AsyncResult<OutlookSyncResult>> {
    const timestamp = new Date().toISOString();
    
    try {
      // Check if event is synced to Outlook
      if (event.externalSync?.provider !== 'microsoft' || !event.externalSync.externalEventId) {
        return {
          success: false,
          error: createError(
            'API_ERROR',
            'Event is not synced to Outlook',
            'Please create the Outlook meeting first',
            true
          ),
        };
      }
      
      const tokenResult = await this.authService.getValidAccessToken();
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }
      
      const outlookEventId = event.externalSync.externalEventId;
      
      // Update the event to enable Teams meeting
      const response = await fetch(
        `${GRAPH_API}/me/events/${outlookEventId}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${tokenResult.data}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            isOnlineMeeting: true,
            onlineMeetingProvider: 'teamsForBusiness',
          }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}`);
      }
      
      const updatedEvent: OutlookCalendarEvent = await response.json();
      
      const teamsMeetingUrl = updatedEvent.onlineMeeting?.joinUrl;
      
      const syncInfo: ExternalSyncInfo = {
        ...event.externalSync,
        lastSyncedAt: timestamp,
        externalLastModified: updatedEvent.lastModifiedDateTime,
        syncStatus: 'synced',
      };
      
      this.log('info', 'attachTeamsMeeting', event.id, outlookEventId,
        `Teams meeting attached: ${teamsMeetingUrl}`);
      
      return {
        success: true,
        data: {
          ax1EventId: event.id,
          outlookEventId,
          teamsMeetingUrl,
          operation: 'updated',
          message: 'Teams meeting link attached',
          syncInfo,
          timestamp,
        },
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'attachTeamsMeeting', event.id, undefined, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to attach Teams meeting',
          errorMessage,
          true
        ),
      };
    }
  }

  /**
   * Syncs multiple AX1 meetings to Outlook Calendar.
   * 
   * @param events - Array of AX1 events to sync
   * @param attachTeamsMeetings - Whether to attach Teams links to all meetings
   * @returns Batch sync result
   */
  public async syncMeetingsToOutlook(
    events: CalendarEvent[],
    attachTeamsMeetings = true
  ): Promise<AsyncResult<OutlookBatchSyncResult>> {
    const startedAt = new Date().toISOString();
    const results: OutlookSyncResult[] = [];
    
    let created = 0;
    let updated = 0;
    let skipped = 0;
    let errors = 0;
    
    this.log('info', 'syncMeetingsToOutlook', undefined, undefined,
      `Starting batch sync of ${events.length} events`);
    
    try {
      // Process events sequentially to avoid rate limiting
      for (const event of events) {
        const result = await this.createOutlookMeeting(event, attachTeamsMeetings);
        
        if (result.success) {
          results.push(result.data);
          
          switch (result.data.operation) {
            case 'created':
              created++;
              break;
            case 'updated':
              updated++;
              break;
            case 'skipped':
              skipped++;
              break;
            case 'error':
              errors++;
              break;
          }
        } else {
          errors++;
          results.push({
            ax1EventId: event.id,
            operation: 'error',
            message: result.error.message,
            timestamp: new Date().toISOString(),
          });
        }
        
        // Small delay to avoid rate limiting
        await this.delay(100);
      }
      
      const completedAt = new Date().toISOString();
      
      this.log('info', 'syncMeetingsToOutlook', undefined, undefined,
        `Batch sync completed: ${created} created, ${updated} updated, ${skipped} skipped, ${errors} errors`);
      
      return {
        success: true,
        data: {
          total: events.length,
          created,
          updated,
          skipped,
          errors,
          results,
          startedAt,
          completedAt,
        },
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'syncMeetingsToOutlook', undefined, undefined, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Batch sync to Outlook failed',
          errorMessage,
          true
        ),
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Read Outlook Changes (Read-Only)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Fetches changes from Outlook for synced events.
   * Returns read-only data - Outlook edits do NOT modify AX1 events.
   * 
   * @param ax1Events - AX1 events that have been synced to Outlook
   * @returns Map of AX1 event ID to Outlook event data (for display only)
   */
  public async fetchOutlookEventStatus(
    ax1Events: CalendarEvent[]
  ): Promise<AsyncResult<Map<string, OutlookEventStatus>>> {
    const statusMap = new Map<string, OutlookEventStatus>();
    
    // Filter to only events synced to Outlook
    const syncedEvents = ax1Events.filter(
      e => e.externalSync?.provider === 'microsoft' && e.externalSync?.externalEventId
    );
    
    if (syncedEvents.length === 0) {
      return { success: true, data: statusMap };
    }
    
    try {
      const tokenResult = await this.authService.getValidAccessToken();
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error };
      }
      
      for (const ax1Event of syncedEvents) {
        const outlookEventId = ax1Event.externalSync!.externalEventId;
        
        try {
          const response = await fetch(
            `${GRAPH_API}/me/events/${outlookEventId}?$select=id,subject,start,end,isCancelled,onlineMeeting,lastModifiedDateTime`,
            {
              headers: {
                Authorization: `Bearer ${tokenResult.data}`,
              },
            }
          );
          
          if (response.status === 404) {
            statusMap.set(ax1Event.id, {
              exists: false,
              deleted: true,
              outlookEventId,
            });
            continue;
          }
          
          if (!response.ok) {
            continue; // Skip on error
          }
          
          const outlookEvent: OutlookCalendarEvent = await response.json();
          
          statusMap.set(ax1Event.id, {
            exists: true,
            deleted: false,
            outlookEventId,
            isCancelled: outlookEvent.isCancelled,
            teamsMeetingUrl: outlookEvent.onlineMeeting?.joinUrl,
            lastModified: outlookEvent.lastModifiedDateTime,
            outlookSubject: outlookEvent.subject,
          });
          
        } catch {
          // Skip individual event errors
          continue;
        }
        
        await this.delay(50);
      }
      
      return { success: true, data: statusMap };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to fetch Outlook event status',
          errorMessage,
          true
        ),
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Public API: Logging
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Gets the sync log entries.
   */
  public getSyncLog(limit = 100): OutlookSyncLogEntry[] {
    return this.syncLog.slice(-limit).reverse();
  }

  /**
   * Clears the sync log.
   */
  public clearSyncLog(): void {
    this.syncLog = [];
    this.log('info', 'clearSyncLog', undefined, undefined, 'Sync log cleared');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Create/Update
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Creates a new meeting in Outlook Calendar.
   */
  private async createOutlookMeetingInternal(
    event: CalendarEvent,
    attachTeamsMeeting: boolean,
    accessToken: string
  ): Promise<AsyncResult<OutlookSyncResult>> {
    const timestamp = new Date().toISOString();
    
    try {
      const outlookEvent = convertToOutlookEvent(event, attachTeamsMeeting);
      
      const response = await fetch(
        `${GRAPH_API}/me/events`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(outlookEvent),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}`);
      }
      
      const createdEvent: OutlookCalendarEvent = await response.json();
      
      const teamsMeetingUrl = createdEvent.onlineMeeting?.joinUrl;
      
      const syncInfo: ExternalSyncInfo = {
        provider: 'microsoft',
        externalEventId: createdEvent.id!,
        syncDirection: 'ax1_to_external',
        lastSyncedAt: timestamp,
        externalLastModified: createdEvent.lastModifiedDateTime,
        syncStatus: 'synced',
      };
      
      this.log('info', 'createOutlookMeeting', event.id, createdEvent.id,
        `Created meeting: ${event.title}${teamsMeetingUrl ? ' (with Teams)' : ''}`);
      
      return {
        success: true,
        data: {
          ax1EventId: event.id,
          outlookEventId: createdEvent.id,
          teamsMeetingUrl,
          operation: 'created',
          message: teamsMeetingUrl 
            ? `Created with Teams meeting: ${teamsMeetingUrl}` 
            : 'Created without Teams meeting',
          syncInfo,
          timestamp,
        },
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'createOutlookMeeting', event.id, undefined, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to create Outlook meeting',
          errorMessage,
          true
        ),
      };
    }
  }

  /**
   * Updates an existing meeting in Outlook Calendar.
   */
  private async updateOutlookMeetingInternal(
    event: CalendarEvent,
    outlookEventId: string,
    attachTeamsMeeting: boolean,
    accessToken: string
  ): Promise<AsyncResult<OutlookSyncResult>> {
    const timestamp = new Date().toISOString();
    
    try {
      const outlookEvent = convertToOutlookEvent(event, attachTeamsMeeting);
      
      const response = await fetch(
        `${GRAPH_API}/me/events/${outlookEventId}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(outlookEvent),
        }
      );
      
      // If event not found, create a new one
      if (response.status === 404) {
        this.log('warn', 'updateOutlookMeeting', event.id, outlookEventId,
          'Event not found in Outlook, creating new one');
        return this.createOutlookMeetingInternal(event, attachTeamsMeeting, accessToken);
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}`);
      }
      
      const updatedEvent: OutlookCalendarEvent = await response.json();
      
      const teamsMeetingUrl = updatedEvent.onlineMeeting?.joinUrl;
      
      const syncInfo: ExternalSyncInfo = {
        provider: 'microsoft',
        externalEventId: updatedEvent.id!,
        syncDirection: 'ax1_to_external',
        lastSyncedAt: timestamp,
        externalLastModified: updatedEvent.lastModifiedDateTime,
        syncStatus: 'synced',
      };
      
      this.log('info', 'updateOutlookMeeting', event.id, updatedEvent.id,
        `Updated meeting: ${event.title}`);
      
      return {
        success: true,
        data: {
          ax1EventId: event.id,
          outlookEventId: updatedEvent.id,
          teamsMeetingUrl,
          operation: 'updated',
          message: 'Meeting updated in Outlook',
          syncInfo,
          timestamp,
        },
      };
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      this.log('error', 'updateOutlookMeeting', event.id, outlookEventId, errorMessage);
      
      return {
        success: false,
        error: createError(
          'API_ERROR',
          'Failed to update Outlook meeting',
          errorMessage,
          true
        ),
      };
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Methods: Logging & Utilities
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Adds an entry to the sync log.
   */
  private log(
    level: OutlookSyncLogEntry['level'],
    operation: string,
    eventId?: string,
    outlookEventId?: string,
    message?: string,
    details?: Record<string, unknown>
  ): void {
    const entry: OutlookSyncLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      operation,
      eventId,
      outlookEventId,
      message: message || '',
      details,
    };
    
    this.syncLog.push(entry);
    
    // Trim log if too large
    if (this.syncLog.length > this.MAX_LOG_ENTRIES) {
      this.syncLog = this.syncLog.slice(-this.MAX_LOG_ENTRIES);
    }
    
    // Also log to console in development
    const consoleMethod = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    consoleMethod(`[OutlookSync] ${operation}: ${message}`, { eventId, outlookEventId, details });
  }

  /**
   * Simple delay helper.
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Additional Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Status of an Outlook event (for read-only display).
 */
export interface OutlookEventStatus {
  /** Whether the event exists in Outlook */
  exists: boolean;
  
  /** Whether the event was deleted */
  deleted: boolean;
  
  /** Outlook event ID */
  outlookEventId: string;
  
  /** Whether cancelled in Outlook */
  isCancelled?: boolean;
  
  /** Teams meeting URL if available */
  teamsMeetingUrl?: string;
  
  /** Last modified timestamp in Outlook */
  lastModified?: string;
  
  /** Subject in Outlook (may differ from AX1 if edited) */
  outlookSubject?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience Functions (Facade)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a meeting in Outlook Calendar with Teams link.
 * 
 * @param event - AX1 calendar event (must be type 'meeting')
 * @param attachTeamsMeeting - Whether to attach Teams meeting link (default: true)
 * @returns Sync result with Outlook event ID and Teams URL
 */
export async function createOutlookMeeting(
  event: CalendarEvent,
  attachTeamsMeeting = true
): Promise<AsyncResult<OutlookSyncResult>> {
  const service = OutlookCalendarService.getInstance();
  return service.createOutlookMeeting(event, attachTeamsMeeting);
}

/**
 * Attaches a Teams meeting link to an existing Outlook event.
 * 
 * @param event - AX1 event with existing Outlook sync info
 * @returns Updated sync result with Teams URL
 */
export async function attachTeamsMeeting(
  event: CalendarEvent
): Promise<AsyncResult<OutlookSyncResult>> {
  const service = OutlookCalendarService.getInstance();
  return service.attachTeamsMeeting(event);
}

/**
 * Syncs multiple AX1 meetings to Outlook Calendar.
 * 
 * @param events - Array of AX1 events to sync
 * @param attachTeamsMeetings - Whether to attach Teams links
 * @returns Batch sync result
 */
export async function syncMeetingsToOutlook(
  events: CalendarEvent[],
  attachTeamsMeetings = true
): Promise<AsyncResult<OutlookBatchSyncResult>> {
  const service = OutlookCalendarService.getInstance();
  return service.syncMeetingsToOutlook(events, attachTeamsMeetings);
}

/**
 * Fetches read-only status of Outlook events.
 * Outlook edits do NOT modify AX1 events.
 * 
 * @param ax1Events - AX1 events synced to Outlook
 * @returns Map of event status
 */
export async function fetchOutlookEventStatus(
  ax1Events: CalendarEvent[]
): Promise<AsyncResult<Map<string, OutlookEventStatus>>> {
  const service = OutlookCalendarService.getInstance();
  return service.fetchOutlookEventStatus(ax1Events);
}

/**
 * Gets the Outlook sync log entries.
 */
export function getOutlookSyncLog(limit?: number): OutlookSyncLogEntry[] {
  const service = OutlookCalendarService.getInstance();
  return service.getSyncLog(limit);
}

// Default export
export default OutlookCalendarService;
